import { useState } from "react"
import { Check, ChevronDown, Library, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ContextSelectDialog } from "@/components/assistant/ContextSelectDialog"
import { useAssistantStore } from "@/stores/assistant-store"
import { useBidsStore } from "@/stores/bids-store"
import { cn } from "@/lib/utils"

type ContextScope = "auto" | "fact_bank" | "documents"

export function ContextScopeMenu() {
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const { context, setContext, grantBidId, setGrantBidId } = useAssistantStore()
  const bids = useBidsStore((state) => state.bids)

  const selectedDocuments = context.selectedDocuments ?? []
  const libraryCount = selectedDocuments.filter((d) => d.type === "library").length

  const scope: ContextScope = context.factBankOnly
    ? "fact_bank"
    : libraryCount > 0
      ? "documents"
      : "auto"

  // Active grant (a bid added to Sources). The label surfaces it alongside the
  // library scope; a grant with specific docs shows the doc count, whole-bid
  // shows "Bid: <title>".
  const grantedBid = grantBidId ? bids.find((b) => b.id === grantBidId) : undefined
  const grantDocCount = grantBidId
    ? selectedDocuments.filter((d) => d.type === "bid" && d.bidId === grantBidId).length
    : 0
  const grantTitle = grantedBid?.title ?? "Bid"
  const grantLabel = grantBidId
    ? grantDocCount > 0
      ? `${grantTitle} · ${grantDocCount} docs`
      : `Bid: ${grantTitle}`
    : null

  const scopeLabel =
    scope === "fact_bank"
      ? "Fact Bank"
      : scope === "documents"
        ? `${libraryCount} context`
        : context.allLibrary
          ? "All Library"
          : "Context"

  // The grant takes the trigger label when present (the notable state); the
  // library scope stays reachable in the dropdown.
  const label = grantLabel ?? scopeLabel
  const hasGrant = grantBidId != null

  const applyAutoScope = () => {
    setContext({ ...context, factBankOnly: false, selectedDocuments: [], allLibrary: true })
    setGrantBidId(null)
  }

  const applyFactBankScope = () => {
    setContext({ ...context, factBankOnly: true, selectedDocuments: [], allLibrary: false })
    setGrantBidId(null)
  }

  return (
    <>
      <div className="flex items-center gap-0.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              data-scope={hasGrant ? "granted" : scope}
              aria-label={label}
              className={cn(
                "h-7 text-[13px]",
                scope === "fact_bank" || hasGrant
                  ? "text-[hsl(var(--brand))] font-medium hover:text-[hsl(var(--brand))]"
                  : scope === "documents" || context.allLibrary
                    ? "text-foreground font-medium hover:text-foreground"
                    : "text-muted-foreground hover:text-foreground"
              )}
            >
              {/* Icon + chevron only on phones, so the composer's send button keeps its room. */}
              <Library className="h-3.5 w-3.5 sm:mr-1" />
              <span className="hidden sm:inline">{label}</span>
              <ChevronDown className="h-3 w-3 ml-0.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {hasGrant && (
              <>
                <div className="px-2 py-1.5">
                  <div className="text-sm font-medium text-[hsl(var(--brand))]">{grantLabel}</div>
                  <div className="text-meta">
                    {grantDocCount > 0
                      ? "Those documents only. No access to drafts."
                      : "Also reads this bid's tender and your work on it."}
                  </div>
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={applyAutoScope} className="items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm">Let AI pick</div>
                <div className="text-meta">
                  Searches and reads your whole library.
                </div>
              </div>
              {scope === "auto" && <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={applyFactBankScope} className="items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm">Fact Bank only</div>
                <div className="text-meta">
                  Answers only from your verified company facts.
                </div>
              </div>
              {scope === "fact_bank" && <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setIsPickerOpen(true)} className="items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm">Specific documents…</div>
                <div className="text-meta">
                  {libraryCount > 0
                    ? `${libraryCount} selected. Answers use only these.`
                    : "Pick from Library and Bids."}
                </div>
              </div>
              {scope === "documents" && <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {(libraryCount > 0 || hasGrant) && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Clear context"
            className="h-5 w-5 text-muted-foreground hover:text-foreground"
            onClick={applyAutoScope}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      <ContextSelectDialog open={isPickerOpen} onOpenChange={setIsPickerOpen} />
    </>
  )
}
